import { useState } from "react";
import { Check, Clock, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { CallButton, useCallsAvailable } from "@/components/call/CallButton";

const RequestDialog = ({ member, open, onOpenChange, onSent }) => {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post("/connections", { user_id: member.id, message }); onSent(data); onOpenChange(false); toast.success(data.state === "connected" ? `You're now connected with ${member.name}` : "Request sent"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-border bg-card" data-testid="connect-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">Connect with {member.name}</DialogTitle>
          <DialogDescription>Add a short note so they know why you'd like to connect (optional).</DialogDescription>
        </DialogHeader>
        <textarea rows={3} maxLength={300} className="field py-3" placeholder="Hi, I saw we both love villa weekends…" value={message}
          onChange={(e) => setMessage(e.target.value)} aria-label="Message with your request" data-testid="connect-message-input" />
        <button type="button" className="btn-primary" disabled={busy} onClick={send} data-testid="connect-send-button">Send request</button>
      </DialogContent>
    </Dialog>
  );
};

export const ConnectButton = ({ member, onChange, compact = false }) => {
  const callsOn = useCallsAvailable();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const act = async (fn, msg) => {
    setBusy(true);
    try { const { data } = await fn(); onChange(data); if (msg) toast.success(msg); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const id = member.id;
  const cls = compact ? "btn-outline h-10 px-4 text-sm" : "btn-outline";
  switch (member.connection_state) {
    case "self": return null;
    case "connected":
      return (
        <span className="inline-flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-sm text-primary" data-testid={`connected-badge-${id}`}><Check className="h-4 w-4" /> Connected</span>
          {callsOn && <CallButton member={member} compact={compact} />}
        </span>
      );
    case "pending_out":
      return (
        <button type="button" className={cls} disabled={busy} onClick={() => act(() => api.delete(`/connections/${member.connection_id}`), "Request withdrawn")}
          title="Withdraw request" data-testid={`connect-pending-${id}`}><Clock className="h-4 w-4" /> Requested</button>
      );
    case "pending_in":
      return (
        <span className="inline-flex gap-2">
          <button type="button" className={compact ? "btn-primary h-10 px-4 text-sm" : "btn-primary"} disabled={busy} data-testid={`connect-accept-${id}`}
            onClick={() => act(() => api.post(`/connections/${member.connection_id}/respond`, { accept: true }), `You're now connected with ${member.name}`)}><Check className="h-4 w-4" /> Accept</button>
          <button type="button" className={cls} disabled={busy} aria-label={`Decline ${member.name}`} data-testid={`connect-decline-${id}`}
            onClick={() => act(() => api.post(`/connections/${member.connection_id}/respond`, { accept: false }), "Request declined")}><X className="h-4 w-4" /></button>
        </span>
      );
    default:
      return (
        <>
          <button type="button" className={compact ? "btn-primary h-10 px-4 text-sm" : "btn-primary"} onClick={() => setOpen(true)} data-testid={`connect-button-${id}`}><UserPlus className="h-4 w-4" /> Connect</button>
          <RequestDialog member={member} open={open} onOpenChange={setOpen} onSent={onChange} />
        </>
      );
  }
};
